import axios, { AxiosRequestConfig } from "axios";
import config from "../config/config";
import tokenService from "./tokenService";

export interface EnhancedRequestConfig extends AxiosRequestConfig {
  onProgress?: (progress: number) => void;
}

let restClient: any;
try {
  if (typeof axios.create === "function") {
    restClient = axios.create({
      baseURL: config.api.rest,
      headers: {
        "Content-Type": "application/json",
      },
    });
  } else {
    // Fallback minimal client for test environments where axios is mocked improperly
    restClient = {
      interceptors: { request: { use: () => {} }, response: { use: () => {} } },
      post: async () => ({ data: {} }),
    } as any;
  }
} catch (err) {
  // If axios.create throws for some reason (test env), provide a safe fallback
  restClient = {
    interceptors: { request: { use: () => {} }, response: { use: () => {} } },
    post: async () => ({ data: {} }),
  } as any;
}

restClient.interceptors.request.use(
  async (config: any) => {
    const token = await tokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

restClient.interceptors.response.use(
  (response: any) => response,
  async (error: any) => {
    const originalRequest: any = error.config || {};

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      console.error("[REST call] Unauthorized error:", error);

      // If a refresh is already in-flight, wait for it. Otherwise start one via the auth flow.
      if (!tokenService.getRefreshPromise()) {
        window.dispatchEvent(new CustomEvent("force-token-refresh"));
      }

      const refreshResult = await tokenService.waitForRefreshIfAny();
      if (refreshResult) {
        // Mark retry to avoid loops
        originalRequest._retry = true;
        const newToken = await tokenService.getAccessToken();
        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return restClient(originalRequest);
        }
      }

      // Refresh failed -> dispatch auth-error
      window.dispatchEvent(
        new CustomEvent("auth-error", {
          detail: { message: "Session expired. Please log in again." },
        })
      );
    }

    return Promise.reject(error);
  }
);

const enhancedClient = {
  // Original methods remain for backward compatibility
  ...restClient,

  // Enhanced post with progress tracking
  postWithProgress: async (
    url: string,
    data: any,
    config: EnhancedRequestConfig = {}
  ) => {
    const { onProgress, ...axiosConfig } = config;

    if (data instanceof FormData) {
      // Set headers properly - this is the critical fix
      axiosConfig.headers = {
        ...axiosConfig.headers,
        "Content-Type": "multipart/form-data", // Let browser set the boundary parameter
      };
    }

    if (onProgress) {
      console.log("Setting up progress handler for:", url);

      axiosConfig.onUploadProgress = (progressEvent) => {
        console.log("Raw progress event:", {
          loaded: progressEvent.loaded,
          total: progressEvent.total,
        });

        if (progressEvent.total) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          console.log(`Progress update: ${percentCompleted}%`);
          onProgress(percentCompleted);
        }
      };
    }

    return restClient.post(url, data, axiosConfig);
  },
  post: (url: string, data: any, config: AxiosRequestConfig = {}) => {
    // Important: For FormData, remove content-type to let browser set it with boundary
    if (data instanceof FormData) {
      config.headers = {
        ...config.headers,
        "Content-Type": "multipart/form-data",
      };
    }

    return restClient.post(url, data, config);
  },
};

export default enhancedClient;
